import React, { useEffect, useRef, useState } from "react";

import styles from "../../css/ai-mode/ai-mode.module.css";
import ConversationApi from "../../api/platform/conversationApi";

import useChatScroll from '../../hooks/useChatScroll';
import { ConversationModel } from "../../models/platform/conversationModel";
import { MessageUserModel } from "../../models/platform/messageUserModel";
import { MessageAssistantModel } from "../../models/platform/messageAssistantModel";
import { useSystemError } from "../../components/Error/SystemErrorContext";
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from "../../utils/errorUtils";
import { ResponseErrorModel } from "../../models/system/responseErrorModel";
import { ResponseSuccessModel } from "../../models/system/responseSuccessModel";
import ChatInput from "../../components/AIMode/ChatInput/ChatInput";
import Sidebar from "../../components/AIMode/Sidebar/Sidebar";
import ChatWindow from "../../components/AIMode/ChatWindow/ChatWindow";

const isMobile = () => window.innerWidth <= 768;

interface AIModeProps {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  onCloseSidebar: () => void;
}

const AIMode: React.FC<AIModeProps> = ({ isSidebarOpen, onToggleSidebar, onCloseSidebar }) => {
  const [conversations, setConversations] = useState<ConversationModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [currentlyEditingId, setCurrentlyEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const [isBotTyping, setIsBotTyping] = useState(false);

  const [activeConversation, setActiveConversation] = useState<ConversationModel | null>(null);
  const [activeMessages, setActiveMessages] = useState<Array<MessageUserModel | MessageAssistantModel>>([]);
  const [inputText, setInputText] = useState<string>("");
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useChatScroll<HTMLDivElement>([activeMessages.length]);

  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await ConversationApi.getConversationsByAccount();
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as ConversationModel[];
        setConversations(data);
        setErrorMessage(null);
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), setErrorMessage, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), setErrorMessage, setDidErrorOccur, triggerSystemError);
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (conversationId: string) => {
    const activeId = activeConversation?._id;
    if (activeId) {
      setDrafts((prev) => ({ ...prev, [activeId]: inputText }));
    }
    if (isMobile()) onCloseSidebar();
    try {
      const response = await ConversationApi.getConversationById(conversationId);
      if (response.success) {
        const data = (response as ResponseSuccessModel).data as ConversationModel;
        setActiveConversation(data);
        setActiveMessages(data.messages || []);
        const restoredText = drafts[conversationId] || "";
        setInputText(restoredText);
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.style.height = "auto";
            inputRef.current.style.height = inputRef.current.scrollHeight + "px";
          }
        });
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), setErrorMessage, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), setErrorMessage, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleDeleteConversation = async (conversationId: string | null) => {
    if (!conversationId) return;
    setConversations((prev) => prev.filter((c) => c._id !== conversationId));
    if (activeConversation?._id === conversationId) {
      setActiveConversation(null);
      setActiveMessages([]);
    }
    handleMenuClose();
    try {
      await ConversationApi.deleteConversation(conversationId);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), setErrorMessage, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, conversationId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedConversationId(conversationId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedConversationId(null);
  };

  const handleNewChat = () => {
    const activeId = activeConversation?._id;
    if (activeId) {
      setDrafts((prev) => ({ ...prev, [activeId]: inputText }));
    }
    setActiveConversation(null);
    setActiveMessages([]);
    setInputText("");
    if (isMobile()) onCloseSidebar();
  };

  const handleRename = () => {
    if (selectedConversationId) {
      handleMenuClose();
      setTimeout(() => {
        setCurrentlyEditingId(selectedConversationId);
        const current = conversations.find((c) => c._id === selectedConversationId);
        setEditingName(current?.conversation_name || "");
      }, 100);
    }
  };

  const handleSaveName = async (conversationId: string) => {
    if (!editingName.trim()) {
      setCurrentlyEditingId(null);
      return;
    }
    try {
      await ConversationApi.updateConversationName(conversationId, editingName.trim());
      const updated = conversations.map((conv) =>
        conv._id === conversationId ? { ...conv, conversation_name: editingName.trim() } : conv
      );
      setConversations(updated);
    } catch (error) {
      console.error("Failed to rename conversation", error);
    }
    setCurrentlyEditingId(null);
  };

  const handleSendMessageStream = async () => {
    if (!inputText.trim()) return;

    const userMessage: MessageUserModel = {
      role: "user",
      user_input: inputText.trim(),
      normalized_input: inputText.trim(),
      interpreted_intent: "",
      intent: "general",
      search_criteria: {},
    };

    setActiveMessages((prev) => [...prev, userMessage]);
    setIsBotTyping(true);
    setInputText("");
    if (inputRef.current) inputRef.current.style.height = "auto";

    try {
      const conversationId = activeConversation?._id ?? null;
      let assistantText = "";
      let streamStarted = false;
      const assistantMessage: MessageAssistantModel = {
        role: "assistant",
        response_text: "",
        summary: "",
        suggested_question: "",
      };
      setActiveMessages((prev) => [...prev, assistantMessage]);

      await ConversationApi.upsertMessageStream(
        conversationId,
        userMessage.user_input,
        (text) => {
          assistantText += text;
          if (!streamStarted) { setIsBotTyping(false); streamStarted = true; }
          setActiveMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], response_text: assistantText };
            return updated;
          });
        },
        ({ summary, suggested_question, assistant_message_id, conversation_id, blocks }) => {
          setIsBotTyping(false);
          if (conversation_id) {
            setActiveConversation(prev => prev ?? { _id: conversation_id } as ConversationModel);
            setConversations(prevConversations => {
              const existing = prevConversations.find(c => c._id === conversation_id);
              if (!existing) {
                return [{
                  _id: conversation_id, messages: [],
                  conversation_name: userMessage.user_input.slice(0, 40) || "Untitled",
                  created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
                  user_id: "", account_id: "", is_deleted: false,
                } as ConversationModel, ...prevConversations];
              }
              return prevConversations;
            });
          }
          setActiveMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              response_text: assistantText, summary, suggested_question,
              _id: assistant_message_id, blocks: blocks ?? []
            };
            return updated;
          });
        },
        (error) => {
          console.error(error);
          setActiveMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], response_text: "Something went wrong. Please try again." };
            return updated;
          });
        }
      );
    } catch (error) {
      setActiveMessages((prev) => [...prev, { role: "assistant", response_text: "An unexpected error occurred.", summary: "", suggested_question: "" }]);
      handleError(mapUnknownErrorToSystemError(error, false), setErrorMessage, setDidErrorOccur, triggerSystemError);
    } finally {
      setIsBotTyping(false);
    }
  };

  return (
    <div className={styles.aiModeContainer}>
      {/* Overlay backdrop on mobile when sidebar is open */}
      {isSidebarOpen && isMobile() && (
        <div className={styles.sidebarOverlay} onClick={onCloseSidebar} />
      )}

      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        conversations={conversations}
        loading={loading}
        errorMessage={errorMessage}
        activeConversationId={activeConversation?._id || null}
        currentlyEditingId={currentlyEditingId}
        editingName={editingName}
        menuAnchorEl={menuAnchorEl}
        selectedConversationId={selectedConversationId}
        onToggle={onToggleSidebar}
        onNewChat={handleNewChat}
        onSelectConversation={loadConversation}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        onRename={handleRename}
        onSaveName={handleSaveName}
        onDelete={handleDeleteConversation}
        onEditingNameChange={setEditingName}
      />

      {/* Main Chat */}
      <main className={styles.aiMain}>
        <ChatWindow
          messages={activeMessages}
          isBotTyping={isBotTyping}
          activeConversationId={activeConversation?._id || null}
          scrollRef={scrollRef}
        />
        <ChatInput
          value={inputText}
          onChange={setInputText}
          onSend={handleSendMessageStream}
        />
      </main>
    </div>
  );
};

export default AIMode;
